import type { Exam } from '@/types/exam';

/**
 * Where the office's exam timetable lands on a timetable grid — the teacher's
 * week, and the child's and parent's class week.
 *
 * Read from the same list as the Exams page, so a paper that page shows on the
 * 3rd is the paper the grid shows on the 3rd — two reads of the schedule would
 * be two answers free to disagree. Only published papers: a draft is its
 * author's to finish, not a date anybody has been given.
 *
 * Every time here is "HH:MM", 24-hour and zero-padded, as both periods and
 * papers store them, so plain string comparison orders them correctly.
 */

/** Just the timing a paper needs to be placed in a period. */
export interface TimedPaper {
  /** "09:30", 24-hour. Null when the office has not recorded timings yet. */
  startTime: string | null;
  endTime: string | null;
}

/** The week's published papers, grouped by the day they are sat. */
export function examsByDate(exams: readonly Exam[], dates: readonly string[]): Map<string, Exam[]> {
  const inWeek = new Set(dates);
  const byDate = new Map<string, Exam[]>();

  for (const exam of exams) {
    if (exam.publication !== 'PUBLISHED' || !inWeek.has(exam.examDate)) continue;
    byDate.set(exam.examDate, [...(byDate.get(exam.examDate) ?? []), exam]);
  }
  for (const papers of byDate.values()) {
    papers.sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? '') || a.name.localeCompare(b.name));
  }

  return byDate;
}

/**
 * The papers sat during one period.
 *
 * A paper fills every period its sitting overlaps — a three-hour paper covers
 * three periods, not just the one it starts in. One with a start but no end
 * shows in the period it starts in, which is all that is known about it.
 */
export function examsInPeriod<T extends TimedPaper>(
  exams: readonly T[],
  period: { timeStart: string; timeEnd: string },
): T[] {
  return exams.filter((exam) => {
    if (!exam.startTime) return false;
    if (!exam.endTime) return exam.startTime >= period.timeStart && exam.startTime < period.timeEnd;
    return exam.startTime < period.timeEnd && exam.endTime > period.timeStart;
  });
}

/** Papers with no recorded time: they belong to the day, not to any period. */
export function untimedExams<T extends TimedPaper>(exams: readonly T[]): T[] {
  return exams.filter((exam) => !exam.startTime);
}
